import { useState } from "react";
import { Ban, Check, X } from "lucide-react";
import { Modal } from "../Modal";
import { Pairs, RecordSheet, SectionBar } from "../RecordSections";
import { formatSchoolDate } from "../students/studentDisplay";
import { apiErrorMessage } from "../../utils/apiError";
import type { LeaveRequest, LeaveType, StaffProfile } from "../../services/staffService";
import { LEAVE_STATUS_DOTS, leaveBalance } from "../../pages/leaveFilters";

export function LeaveStatus({ status }: { status: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium capitalize text-gray-700">
      <span className={`h-1.5 w-1.5 rounded-full ${LEAVE_STATUS_DOTS[status] || "bg-gray-400"}`} />
      {status}
    </span>
  );
}

/** Approved leave of colleagues in the same department that shares a day with this one. */
export function colleaguesAway(leave: LeaveRequest, leaves: LeaveRequest[], profiles: StaffProfile[]) {
  const departmentOf = new Map(profiles.map((profile) => [profile.id, profile.department]));
  const department = departmentOf.get(leave.staff);
  return leaves.filter(
    (other) =>
      other.staff !== leave.staff &&
      other.status === "approved" &&
      departmentOf.get(other.staff) === department &&
      other.start_date <= leave.end_date &&
      other.end_date >= leave.start_date,
  );
}

export default function LeaveReviewModal({
  leave,
  leaves,
  leaveTypes,
  profiles,
  canDecide,
  canCancel,
  busy,
  error,
  onApprove,
  onReject,
  onCancelRequest,
  onClose,
}: {
  leave: LeaveRequest;
  leaves: LeaveRequest[];
  leaveTypes: LeaveType[];
  profiles: StaffProfile[];
  canDecide: boolean;
  canCancel: boolean;
  busy: boolean;
  error: unknown;
  onApprove: (notes: string) => void;
  onReject: (notes: string) => void;
  onCancelRequest: () => void;
  onClose: () => void;
}) {
  const [notes, setNotes] = useState("");
  const leaveType = leaveTypes.find((type) => type.id === leave.leave_type);
  const balance = leaveBalance(leaves, leave.staff, leaveType, Number(leave.start_date.slice(0, 4)), leave.id);
  const leftAfter = balance ? balance.left - leave.total_days : null;
  const pending = leave.status === "pending";
  const decide = canDecide && pending;
  const away = colleaguesAway(leave, leaves, profiles);
  const department = profiles.find((profile) => profile.id === leave.staff)?.department_name;

  return (
    <Modal
      isOpen
      size="3xl"
      title="Leave request"
      onClose={() => {
        if (!busy) onClose();
      }}
      footer={
        <>
          <button type="button" className="btn btn-outline" disabled={busy} onClick={onClose}>
            Close
          </button>
          {canCancel && pending && (
            <button type="button" className="btn btn-outline gap-2 text-red-700" disabled={busy} onClick={onCancelRequest}>
              <Ban className="h-4 w-4" /> Cancel request
            </button>
          )}
          {decide && (
            <>
              <button
                type="button"
                className="btn btn-outline gap-2 text-red-700"
                disabled={busy || notes.trim().length < 5}
                title={notes.trim().length < 5 ? "Say why in the notes first" : undefined}
                onClick={() => onReject(notes.trim())}
              >
                <X className="h-4 w-4" /> Reject
              </button>
              <button
                type="button"
                className="btn gap-2 bg-green-600 text-white hover:bg-green-700"
                disabled={busy}
                onClick={() => onApprove(notes.trim())}
              >
                <Check className="h-4 w-4" /> {busy ? "Saving…" : "Approve"}
              </button>
            </>
          )}
        </>
      }
    >
      <div className="space-y-4">
        {Boolean(error) && (
          <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
            {apiErrorMessage(error, "The leave request could not be updated. Please try again.")}
          </p>
        )}

        <RecordSheet>
          <header className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <div>
              <h2 className="text-lg font-semibold leading-6 text-gray-900">{leave.staff_name}</h2>
              <p className="text-xs text-gray-500">
                {leave.leave_type_name} · {department || "School-wide"}
              </p>
            </div>
            <div className="text-right">
              <LeaveStatus status={leave.status} />
              <p className="text-sm font-semibold tabular-nums text-gray-900">
                {leave.total_days} day{leave.total_days === 1 ? "" : "s"}
              </p>
            </div>
          </header>

          <SectionBar letter="A" title="Leave" />
          <Pairs
            rows={[
              ["From", formatSchoolDate(leave.start_date)],
              ["To", formatSchoolDate(leave.end_date)],
              ["Type", `${leave.leave_type_name}${leaveType && !leaveType.is_paid ? " (unpaid)" : ""}`],
              ["Requested", leave.created_at && formatSchoolDate(leave.created_at)],
              ["Reason", leave.reason],
            ]}
          />

          {balance && (
            <>
              <SectionBar letter="B" title={`${leave.leave_type_name} balance, ${leave.start_date.slice(0, 4)}`} />
              <dl className="grid grid-cols-2 divide-x divide-gray-200 text-center sm:grid-cols-4">
                {[
                  ["Allowed", balance.allowed, "text-gray-900"],
                  ["Taken", balance.used, "text-gray-900"],
                  ["Other pending", balance.pending, "text-amber-700"],
                  ["Left after this", leftAfter, leftAfter !== null && leftAfter < 0 ? "text-red-700" : "text-green-700"],
                ].map(([label, value, tone]) => (
                  <div key={label as string} className="px-3 py-2">
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
                    <dd className={`text-base font-semibold tabular-nums ${tone}`}>{value}</dd>
                  </div>
                ))}
              </dl>
            </>
          )}

          <SectionBar letter="C" title="Also away in this department" />
          <div className="px-4 py-3">
            {away.length ? (
              <ul className="space-y-1">
                {away.map((other) => (
                  <li key={other.id} className="flex flex-wrap justify-between gap-2">
                    <span className="font-medium text-gray-900">{other.staff_name}</span>
                    <span className="tabular-nums text-gray-600">
                      {other.leave_type_name} · {formatSchoolDate(other.start_date)} to {formatSchoolDate(other.end_date)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500">No one else in the department has approved leave over these days.</p>
            )}
          </div>

          {!pending && (
            <>
              <SectionBar letter="D" title="Decision" />
              <Pairs
                rows={[
                  ["Outcome", <span className="capitalize">{leave.status}</span>],
                  ["Decided by", leave.approved_by_name],
                  ["On", leave.approved_date && formatSchoolDate(leave.approved_date)],
                  ["Notes", leave.approval_notes],
                ]}
              />
            </>
          )}
        </RecordSheet>

        {decide && (
          <label className="block text-sm font-medium text-gray-700">
            Notes
            <textarea
              rows={2}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              className="input mt-1 h-auto"
              placeholder="Optional when approving; needed to reject, e.g. Clashes with end-of-term exams."
            />
            <span className="mt-1 block text-xs font-normal text-gray-500">
              Kept with the decision and shown to {leave.staff_name.split(" ")[0]}.
            </span>
          </label>
        )}
      </div>
    </Modal>
  );
}
